import React, {useState, useCallback, useEffect, useRef} from 'react';
import {useFormik} from 'formik';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {
    getRectangularElementDimension,
    updateDimension,
    getRoomDimensions,
    getTopViewZoom,
    setPreviewPosition,
    getRoomType,
    getLShapedElementDimension,
    getLayoutData,
    setRectangularDimension,
    setLShapedDimension,
    setIsTopView,
    setRoomType,
    setIsEditingRoomDimension,
    setToInitialState,
    getOldCabinetsPosition,
    setHiddenWall,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {shallowEqual, useSelector} from 'react-redux';
import {RoomType} from 'components/customer/RoomPlanner/types';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import styled from 'styled-components';
import {CBCButton} from 'shared/helpers';
import {resetOriginalCabinetPosition} from 'components/customer/RoomPlanner/helpers/resetOriginalCabinetPosition';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {setHiddenWallWithRetry} from 'components/customer/RoomPlanner/helpers/hiddenWallRetry';
import {
    mobileLandscape,
    mobilePortrait,
    tabletLandscape,
    tabletPortrait,
} from 'shared/Breakpoints';
import {useIsTouchDevices} from 'shared/helpers/DeviceSize';
import {validationSchema} from 'components/customer/RoomPlanner/helpers/roomPlannerValidationSchema';

export const FieldError = styled.div<{
    align?: 'left' | 'right';
    position?: 'above';
}>`
    color: red;
    font-size: 12px;
    margin-top: 2px;
    min-height: 16px;
    text-align: ${({align}) => align || 'center'};

    /* Core prevention: Force long text to wrap downward instead of stretching wide */
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;

    /* Pull out of layout flow to prevent shifting the parent container */
    position: absolute;
    ${({position}) =>
        position === 'above'
            ? 'bottom: 100%; top: auto;'
            : 'top: 100%; bottom: auto;'}
    left: 50%;
    transform: translateX(-50%);
    width: max-content;
    max-width: 150px;

    background-color: #ffffff;
    padding: 4px 8px;
    border: 1px solid #ecc9c9;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);

    /* Mobile Portrait view */
    ${mobilePortrait`
        font-size: 11px;
        max-width: 110px;
        line-height: 1.2;
    `}

    /* Mobile Landscape view */
    ${mobileLandscape`
        font-size: 11px;
        max-width: 120px;
        line-height: 1.2;
    `}

    /* Tablet Portrait view */
    ${tabletPortrait`
        font-size: 12px;
        max-width: 130px;
        line-height: 1.3;
    `}
    
    /* Tablet Landscape view */
    ${tabletLandscape`
        font-size: 12px;
        max-width: 140px;
        line-height: 1.3;
    `}
`;

const RoomDimensionOverlay = ({
    previewPositionRef,
}: {
    previewPositionRef: React.MutableRefObject<PreviewPosition>;
}) => {
    const dispatch = useAppDispatch();
    const {cabinetGroupListRef} = useRoomPlannerContext();

    const rectangularElementDimension = useAppSelector(
        getRectangularElementDimension
    );
    const lShapedElementDimension = useAppSelector(getLShapedElementDimension);
    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const topViewZoom = useAppSelector(getTopViewZoom);
    const roomType = useAppSelector(getRoomType);
    const [width, setWidth] = useState(0);
    const [length, setLength] = useState(0);
    const [lShapedWidth, setLShapedWidth] = useState(0);
    const [lShapedLength, setLShapedLength] = useState(0);
    const layoutData = useAppSelector(getLayoutData);
    const oldCabinets = useSelector(getOldCabinetsPosition);

    const isMobileDevice = useIsTouchDevices();

    useEffect(() => {
        if (rectangularElementDimension) {
            let width = rectangularElementDimension?.width;
            let length = rectangularElementDimension?.length;

            if (isMobileDevice) {
                const mainContent =
                    document.querySelector('.component-section');
                const dimension = mainContent.getBoundingClientRect();

                width = dimension.width;
                length = dimension.height;
            }

            setWidth(width * topViewZoom + 25);
            setLength(length * topViewZoom - (length * topViewZoom) / 7.8);
        }
    }, [rectangularElementDimension, topViewZoom, isMobileDevice]);

    useEffect(() => {
        dispatch(setIsEditingRoomDimension(true));
        dispatch(setHiddenWall(null));
    }, [dispatch]);

    useEffect(() => {
        if (lShapedElementDimension) {
            let rightWidth = lShapedElementDimension?.rightWidth;
            let leftWidth = lShapedElementDimension?.leftWidth;

            if (isMobileDevice) {
                const mainContent =
                    document.querySelector('.component-section');
                const dimension = mainContent.getBoundingClientRect();

                rightWidth = dimension.width;
                leftWidth = dimension.height;
            }

            setLShapedWidth(rightWidth * topViewZoom + 25);
            setLShapedLength(
                leftWidth * topViewZoom - (leftWidth * topViewZoom) / 7.8
            );
        }
    }, [lShapedElementDimension, topViewZoom, isMobileDevice]);

    const formik = useFormik({
        initialValues: {
            ...rectangularDimension,
            ...lShapedDimension,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: () => undefined,
    });

    const hasError = (fieldName: keyof typeof formik.values) => {
        /* eslint-disable-next-line security/detect-object-injection --
         * Imposible to fix without using formik.touched and formik.errors
         **/
        return formik.touched[fieldName] && formik.errors[fieldName];
    };
    type DimensionFields = keyof typeof formik.values;

    const formikValuesRef = useRef(formik.values);
    useEffect(() => {
        formikValuesRef.current = formik.values;
    }, [formik.values]);

    const pendingValuesRef = useRef<Partial<typeof formik.values>>({});

    const handleFieldChange = useCallback(
        (fieldName: DimensionFields, roomType: RoomType) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                formik.handleChange(e);
                const value = Number(e.target.value);

                // Accumulate the pending change
                pendingValuesRef.current = {
                    ...pendingValuesRef.current,
                    [fieldName]: value,
                };

                const updatedValues = {
                    ...formikValuesRef.current,
                    ...pendingValuesRef.current,
                };

                void validationSchema
                    .validate(updatedValues, {abortEarly: false})
                    .then(() => {
                        // Dispatch ALL pending changes at once when everything is valid
                        Object.entries(pendingValuesRef.current).forEach(
                            ([field, val]) => {
                                dispatch(
                                    updateDimension({
                                        field,
                                        value: val,
                                        type: roomType,
                                    })
                                );
                            }
                        );
                        pendingValuesRef.current = {};
                    })
                    .catch(() => {
                        void formik.setFieldTouched(fieldName, true);
                    });
            },
        [dispatch]
    );

    const handleConfirmShape = useCallback(() => {
        dispatch(setPreviewPosition('LEFT_LEVELED_STATIC'));
    }, [dispatch]);

    const handleCancelShape = useCallback(() => {
        if (!layoutData) {
            dispatch(setToInitialState());
        } else {
            const layout = {...layoutData};
            const isRectangular = layout.layout_room_shape_id === 1;
            previewPositionRef.current = 'RIGHT';

            if (isRectangular) {
                const {width, length, height} = layout.room_dimensions;

                if (width == null || length == null || height == null) {
                    return;
                }

                dispatch(
                    setRectangularDimension({
                        width,
                        length,
                        height,
                    })
                );
            } else {
                const {leftWidth, rightWidth, leftDepth, rightDepth, height} =
                    layout.room_dimensions;

                if (
                    leftWidth == null ||
                    rightWidth == null ||
                    leftDepth == null ||
                    rightDepth == null ||
                    height == null
                ) {
                    return;
                }

                dispatch(
                    setLShapedDimension({
                        leftWidth,
                        rightWidth,
                        leftDepth,
                        rightDepth,
                        height,
                    })
                );
            }
            dispatch(setIsTopView(false));
            dispatch(setIsEditingRoomDimension(false));
            dispatch(setRoomType(isRectangular ? 'RECTANGULAR' : 'LSHAPED'));
            if (cabinetGroupListRef && oldCabinets) {
                resetOriginalCabinetPosition({
                    cabinetGroupListRef,
                    oldCabinets,
                    autoSave,
                });
            }
        }
        setHiddenWallWithRetry(dispatch, 'rightWall, frontWall');
    }, [dispatch, layoutData]);

    const getInputStyle = (fieldName: keyof typeof formik.values) => {
        return {
            border: hasError(fieldName) ? '1px solid red' : '1px solid #ccc',
            outline: 'none',
            '&:focus': {
                border: hasError(fieldName)
                    ? '1px solid red'
                    : '1px solid #ccc',
            },
        };
    };

    const getLabelStyle = (fieldName: keyof typeof formik.values) => {
        return {
            color: hasError(fieldName) ? 'red' : '#666',
        };
    };

    return (
        <>
            {roomType === 'RECTANGULAR' ? (
                <Rectangle $width={width} $length={length}>
                    {/* Top input */}
                    <TopInputContainer>
                        <Label style={getLabelStyle('width')}>Width</Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="width"
                                value={formik.values.width}
                                onChange={handleFieldChange(
                                    'width',
                                    'RECTANGULAR'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('width')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('width') && (
                            <FieldError>{formik.errors.width}</FieldError>
                        )}
                    </TopInputContainer>

                    {/* Left input */}
                    <LeftInputContainer>
                        <Label style={getLabelStyle('length')}>Length</Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="length"
                                value={formik.values.length}
                                onChange={handleFieldChange(
                                    'length',
                                    'RECTANGULAR'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('length')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('length') && (
                            <FieldError>{formik.errors.length}</FieldError>
                        )}
                    </LeftInputContainer>
                </Rectangle>
            ) : null}

            {roomType === 'LSHAPED' ? (
                <Rectangle $width={lShapedWidth} $length={lShapedLength}>
                    {/* Top input */}
                    <TopInputContainer $isLShaped>
                        <Label style={getLabelStyle('rightWidth')}>
                            Right Width
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="rightWidth"
                                value={formik.values.rightWidth}
                                onChange={handleFieldChange(
                                    'rightWidth',
                                    'LSHAPED'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('rightWidth')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('rightWidth') && (
                            <FieldError>{formik.errors.rightWidth}</FieldError>
                        )}
                    </TopInputContainer>

                    {/* Right input */}
                    <RightInputContainer>
                        <Label style={getLabelStyle('rightDepth')}>
                            Right Depth
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="rightDepth"
                                value={formik.values.rightDepth}
                                onChange={handleFieldChange(
                                    'rightDepth',
                                    'LSHAPED'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('rightDepth')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('rightDepth') && (
                            <FieldError>{formik.errors.rightDepth}</FieldError>
                        )}
                    </RightInputContainer>

                    {/* Bottom input */}
                    <BottomInputContainer>
                        <Label style={getLabelStyle('leftDepth')}>
                            Left Depth
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="leftDepth"
                                value={formik.values.leftDepth}
                                onChange={handleFieldChange(
                                    'leftDepth',
                                    'LSHAPED'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('leftDepth')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('leftDepth') && (
                            <FieldError position="above">
                                {formik.errors.leftDepth}
                            </FieldError>
                        )}
                    </BottomInputContainer>

                    {/* Left input */}
                    <LeftInputContainer>
                        <Label style={getLabelStyle('leftWidth')}>
                            Left Width
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="leftWidth"
                                value={formik.values.leftWidth}
                                onChange={handleFieldChange(
                                    'leftWidth',
                                    'LSHAPED'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('leftWidth')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {hasError('leftWidth') && (
                            <FieldError>{formik.errors.leftWidth}</FieldError>
                        )}
                    </LeftInputContainer>
                </Rectangle>
            ) : null}

            <ButtonContainer>
                <Button
                    onClick={handleCancelShape}
                    iconName="Button-Cancel.svg"
                    className="job-button button-light"
                    style={{color: '#8A8080'}}>
                    Cancel
                </Button>
                <CBCButton
                    type="button"
                    onClick={handleConfirmShape}
                    iconName="Button-Tick.svg"
                    className="job-button button-blue"
                    disabled={Object.keys(formik.errors).length > 0}>
                    Confirm Shape
                </CBCButton>
            </ButtonContainer>
        </>
    );
};

const Rectangle = styled.div<{$width: number; $length: number}>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => props.$width}px;
    height: ${(props) => props.$length}px;
    margin-top: -35px;
`;

const InputContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const Label = styled.span`
    font-size: 12px;
    color: #666;
    font-weight: 500;
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const ArrowInput = styled.input`
    width: 100px;
    text-align: center;
    padding: 4px 25px 4px 4px;
    border: 1px solid #ccc;
    border-radius: 4px;
`;

const InputSuffix = styled.span`
    position: absolute;
    right: 8px;
    color: #666;
    font-size: 12px;
    pointer-events: none;
`;

const ButtonContainer = styled.div`
    position: fixed;
    bottom: 52px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1001;
    display: flex;
    gap: 16px;

    ${mobilePortrait`
        bottom: 5px;
        left: 0;
        right: 0;
        transform: initial;
        padding: 0 15px;

        & > button {
            font-size: 0.8em !important;
            width: 80% !important;
        }    
    `}

    ${mobileLandscape`
        bottom: 10px;
        z-index: 999991;
        left: initial;
        right: 10px;
        transform: initial;
    `}

    ${tabletPortrait`
        bottom: 10px;
        z-index: 999991;
    `}
    
    ${tabletLandscape`
        bottom: 5px;    
        z-index: 999991;
    `}
`;

// FIXME: Use CBCButton instead of inline component when we have
// a proper cypress test.
const Button = styled((props: React.ComponentProps<typeof CBCButton>) => (
    <CBCButton {...props} />
))`
    border-radius: 40px !important;
    padding: 5px 10px 5px 5px !important;
    width: fit-content !important;
    font-size: 0.95em !important;
    &:hover {
        border: 1px solid white;
    }
    @media (max-width: 695px) {
        font-size: 0.8em !important;
        width: 80% !important;
    }
`;

// InputContainer for top input
const TopInputContainer = styled(InputContainer)<{$isLShaped?: boolean}>`
    top: ${(props) => (props.$isLShaped ? '-20px' : '0')};
    left: 50%;
    transform: translateX(-50%) translateY(-100%);
    @media (max-width: 1600px), (max-height: 900px) {
        top: 0;
    }
`;

// InputContainer for left input
const LeftInputContainer = styled(InputContainer)`
    left: 0;
    top: 50%;
    transform: translateY(-50%) translateX(-100%);
`;

// InputContainer for right input
const RightInputContainer = styled(InputContainer)`
    right: 0;
    top: 25%;
    transform: translateY(-50%) translateX(100%);
`;

// InputContainer for bottom input
const BottomInputContainer = styled(InputContainer)`
    bottom: 0;
    left: 25%;
    transform: translateX(-50%) translateY(100%);
    margin-left: 30px;
    @media (max-width: 1600px), (max-height: 900px) {
        bottom: -20px;
    }
`;

export default RoomDimensionOverlay;
